import { Box, Divider, Grid, Typography, useTheme } from "@mui/material"
import moment from "moment/moment"
import FormPreviewFileThumbnail from "../../components/Form/FormComponents/FormUploadFile/FormPreviewFileThumbnail"
import { TextCell2 } from "./components/TextCell"
import { t } from "i18next"

const TechnicalData = ({ data, assemblyData }) => {
  const theme = useTheme()
  return (
    <>
      <Grid container direction="row" spacing={3}>
        <Grid item md={7} xl={8} xs={12} gap={3} display="flex" flexDirection="column">
          <Box>
            <Typography variant="h3" color={theme.palette.grey[900]} marginBottom={2}>
              {t("vehicleregistry.axles_and_wheels", "Axles and wheels")}
            </Typography>
            <AxlesAndWheels data={data} />
          </Box>
          <Divider light />
          <Box>
            <Typography variant="h3" color={theme.palette.grey[900]} marginBottom={2}>
              {t("vehicleregistry.dimensions_and_weight", "Dimensions and weight")}
            </Typography>
            <WeightsAndMeasures data={data} />
          </Box>
          <Divider light />
          <Box>
            <Typography variant="h3" color={theme.palette.grey[900]} marginBottom={2}>
              {t("vehicleregistry.engine_and_environment", "Engine and environment")}
            </Typography>
            <EngineAndEnvironment theme={theme} data={data} />
          </Box>
          <Divider light />
          <Box>
            <Typography variant="h3" color={theme.palette.grey[900]} marginBottom={2}>
              {t("global.passenger", "Passenger")}
            </Typography>
            <Passenger data={data} />
          </Box>
          <Divider light />
          <Box>
            <Typography variant="h3" color={theme.palette.grey[900]} marginBottom={2}>
              {t("vehicleregistry.clutch_and_brakes", "Clutch and brakes")}
            </Typography>
            <CouplingDeviceAndBrakes data={data} />
          </Box>
          <Divider light />
          {assemblyData?.data?.attachments?.length > 0 && (
            <Box>
              <Typography variant="h3" color={theme.palette.grey[900]} marginBottom={2}>
                {t("vehicleregistry.pictures_of_assembly", "Pictures of assembly")}
              </Typography>
              <AssemblyImages data={assemblyData} />
            </Box>
          )}
          <Box>
            <Typography variant="h3" color={theme.palette.grey[900]} marginBottom={2}>
              {t("global.other", "Other")}
            </Typography>
            <Other data={data} />
          </Box>
        </Grid>
        <Grid item md={5} xl={4} xs={12} gap={2} display="flex" flexDirection="column">
          <Box bgcolor="background.light" borderRadius="10px" display="flex" flexDirection="column" p={3}>
            <Grid container direction="row" spacing={2}>
              <Grid item>
                <TextCell2 label={t("vehicleregistry.gearbox", "Gearbox")} text={data.vaxellada} />
              </Grid>
              <Grid item>
                <TextCell2 label={t("global.fuel_1", "Fuel 1")} text={data.drivMedel1Kod} />
              </Grid>
              <Grid item>
                <TextCell2
                  label={t("vehicleregistry.seated_passanger", "Seated passenger")}
                  text={
                    data.fordonstyp === "Personbil" || data.fordonstyp === "Turistbuss Dubbeldäckare"
                      ? data.antalPassagerare + " " + t("global.pc", "pcs")
                      : data.antalSittande && data.antalSittande + " " + t("global.pc", "pcs")
                  }
                />
              </Grid>
              <Grid item>
                <TextCell2
                  label={t("vehicleregistry.total_number_of_passangers", "Total number of passengers")}
                  text={data.antalPassagerare + " " + t("global.pc", "pcs")}
                />
              </Grid>
            </Grid>
          </Box>
          <Box bgcolor="background.light" borderRadius="10px" display="flex" flexDirection="column" p={3}>
            <Grid item>
              <Typography color={theme.palette.grey[900]} variant="h3" mb={2}>
                {t("vehicleregistry.latest_inspection", "Latest inspection")}
              </Typography>
            </Grid>
            <Grid container direction="row" spacing={2}>
              <Grid item>
                <TextCell2
                  label={t("global.inspected", "Inspected")}
                  text={
                    data.besiktningsdatum && (
                      <Typography variant="bodyMedium" fontWeight={600}>
                        {moment.utc(data.besiktningsdatum).local().format("YYYY-MM-DD")}
                      </Typography>
                    )
                  }
                />
              </Grid>
              <Grid item>
                <TextCell2
                  label={t("vehicleregistry.inspection_station", "Inspection station")}
                  text={data.besiktningsstation}
                />
              </Grid>
              <Grid item>
                <TextCell2
                  label={t("vehicleregistry.inspection_program_code", "Inspection program code")}
                  text={data.besiktningsprogramkod}
                />
              </Grid>
              <Grid item>
                <TextCell2 label={t("global.meter_setting", "Meter setting")} text={data.matarstallning} />
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </>
  )
}

const AxlesAndWheels = ({ data }) => {
  return (
    <Grid container>
      <Grid item xs={12} direction="column" display="flex" gap={2}>
        <Grid container direction="row" display="flex" rowSpacing={2} columnSpacing={4}>
          <Grid item>
            <TextCell2 label={t("vehicleregistry.axles", "Axles")} text={data.antalAxlar} />
          </Grid>
          <Grid item>
            <TextCell2
              label={t("vehicleregistry.axles_distance_1", "Axles distance 1")}
              text={data.axelavstanDriv1}
            />
          </Grid>
          <Grid item>
            <TextCell2
              label={t("vehicleregistry.axles_distance_2", "Axles distance 2")}
              text={data.axelavstanDriv2}
            />
          </Grid>
          <Grid item>
            <TextCell2
              label={t("vehicleregistry.axles_distance_3", "Axles distance 3")}
              text={data.axelavstanDriv3}
            />
          </Grid>

          <Grid item>
            <TextCell2
              label={t("vehicleregistry.front_tire_size", "Front tire size")}
              text={data.dackdimensionFram}
            />
          </Grid>
          <Grid item>
            <TextCell2
              label={t("vehicleregistry.rear_tire_size", "Rear tire size")}
              text={data.dackdimensionBak}
            />
          </Grid>

          <Grid item>
            <TextCell2
              label={t("vehicleregistry.front_rim_dimension", "Front rim dimension")}
              text={data.falgdimensionFram}
            />
          </Grid>
          <Grid item>
            <TextCell2
              label={t("vehicleregistry.rear_rim_dimension", "Rear rim dimension")}
              text={data.falgdimensionBak}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

const WeightsAndMeasures = ({ data }) => {
  return (
    <Grid container>
      <Grid item xs={12} direction="column" display="flex" gap={2}>
        <Grid container direction="row" display="flex" rowSpacing={2} columnSpacing={4}>
          <Grid item>
            <TextCell2 label={t("global.width", "Width")} text={data.bredd} />
          </Grid>
          <Grid item>
            <TextCell2 label={t("global.height", "Height")} text={data.hojd} />
          </Grid>
          <Grid item>
            <TextCell2 label={t("global.length", "Length")} text={data.langd} />
          </Grid>

          <Grid item>
            <TextCell2 label={t("vehicleregistry.max_load", "Max load")} text={data.maxLastvikt} />
          </Grid>
          <Grid item>
            <TextCell2
              label={t("vehicleregistry.service_weight", "Service weight")}
              text={data.tjanstevikt}
            />
          </Grid>
          <Grid item>
            <TextCell2 label={t("vehicleregistry.total_weight", "Total weight")} text={data.totalvikt} />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

const EngineAndEnvironment = ({ data, theme }) => {
  return (
    <Grid item xs={12} gap={2} display="flex" flexDirection="column">
      <Grid container direction="row" rowSpacing={2} columnSpacing={4}>
        <Grid item>
          <TextCell2
            label={t("vehicleregistry.eec_approval_number", "EEC Approval Number")}
            text={data.eeg}
          />
        </Grid>
        <Grid item>
          <TextCell2 label={t("vehicleregistry.gearbox", "Gearbox")} text={data.vaxellada} />
        </Grid>
        <Grid item>
          <TextCell2 label={t("vehicleregistry.effect", "Effect")} text={data.driv1Motoreffekt} />
        </Grid>
        <Grid item>
          <TextCell2
            label={t("vehicleregistry.vehicle_category_eu", "Vehicle category EU")}
            text={data.fordonskategoriEU}
          />
        </Grid>

        <Grid item>
          <TextCell2 label={t("global.environmental_class", "Environmental class")} text={data.miljoklass} />
        </Grid>
        <Grid item>
          <TextCell2
            label={t("vehicleregistry.environmental_class", "Environmental class Euro")}
            text={data.miljoklassEuro}
          />
        </Grid>
        <Grid item>
          <TextCell2
            label={t("vehicleregistry.emission_value_unit", "Emission value Unit")}
            text={data.emmisionsvardeEnhet}
          />
        </Grid>
      </Grid>
      <Grid container direction="row" rowSpacing={2} columnSpacing={4}>
        <Grid item>
          <Typography variant="h3" color={theme.palette.grey[900]}>
            {t("vehicleregistry.fuel_1", "Fuel 1")}
          </Typography>
        </Grid>
      </Grid>
      <Grid container direction="row" rowSpacing={2} columnSpacing={4}>
        <Grid item>
          <TextCell2 label={t("global.fuel", "Fuel")} text={data.drivMedel1Kod} />
        </Grid>
        <Grid item>
          <TextCell2 label={t("vehicleregistry.body_code", "Body code")} text={data.karosserikoDriv1} />
        </Grid>

        <Grid item>
          <TextCell2 label={t("global.consumption", "Consumption")} text={data.forbrukningDriv1} />
        </Grid>
        <Grid item>
          <TextCell2 label={t("global.co2")} text={data.CO2Driv1} />
        </Grid>
        <Grid item>
          <TextCell2 label={t("vehicleregistry.no_x", "NOx")} text={data.nOxDriv1} />
        </Grid>
      </Grid>
      <Grid container direction="row" rowSpacing={2} columnSpacing={4}>
        <Grid item>
          <Typography variant="h3" color={theme.palette.grey[900]}>
            {t("vehicleregistry.fuel_2", "Fuel 2")}
          </Typography>
        </Grid>
      </Grid>
      <Grid container direction="row" rowSpacing={2} columnSpacing={4}>
        <Grid item>
          <TextCell2
            label={t("global.fuel", "Fuel")}
            text={data.drivMedel2Kod !== "0" && data.drivMedel2Kod}
          />
        </Grid>

        <Grid item>
          <TextCell2 label={t("vehicleregistry.body_code", "Body code")} text={data.karosserikoDriv2} />
        </Grid>

        <Grid item>
          <TextCell2 label={t("global.consumption", "Consumption")} text={data.forbrukningDriv2} />
        </Grid>
      </Grid>
    </Grid>
  )
}

const AssemblyImages = ({ data }) => {
  return (
    <Grid container direction="row" spacing={2}>
      {data?.data?.attachments.map((attachment) => (
        <Grid item xs={12} md={4}>
          <FormPreviewFileThumbnail
            fileId={attachment.fileId}
            fileName={attachment.fileName}
            Data={attachment.base64}
          />
        </Grid>
      ))}
    </Grid>
  )
}

const Other = ({ data }) => {
  return (
    <Grid container direction="row" rowSpacing={2} columnSpacing={4}>
      <Grid item xs={12}>
        <Grid container direction="row" rowSpacing={2} columnSpacing={4}>
          <Grid item>
            <TextCell2 label={t("vehicleregistry.tank_volume", "Tank volume")} text={data.tankvolymDriv1} />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

const CouplingDeviceAndBrakes = ({ data }) => {
  return (
    <Grid container direction="row" rowSpacing={2} columnSpacing={4}>
      <Grid item xs={12}>
        <Grid container direction="row" rowSpacing={2} columnSpacing={4}>
          <Grid item>
            <TextCell2
              label={t("vehicleregistry.coupling_devices", "Coupling devices")}
              text={data.kopplingsanordning}
            />
          </Grid>
          <Grid item>
            <TextCell2
              label={t("global.equipment", "Equipment")}
              text={`${data.utrustning1}, ${data.utrustning2}, ${data.utrustning3}`}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

const Passenger = ({ data }) => {
  return (
    <Grid container direction="row" rowSpacing={2} columnSpacing={4}>
      <Grid item xs={12}>
        <Grid container direction="row" rowSpacing={2} columnSpacing={4}>
          <Grid item>
            <TextCell2
              label={t("vehicleregistry.seated_passanger", "Seated passenger")}
              text={
                data.fordonstyp === "Personbil" || data.fordonstyp === "Turistbuss Dubbeldäckare"
                  ? data.antalPassagerare + " " + t("global.pc", "pcs")
                  : data.antalSittande && data.antalSittande + " " + t("global.pc", "pcs")
              }
            />
          </Grid>
          <Grid item>
            <TextCell2
              label={t("vehicleregistry.total_number_of_passangers", "Total number of passengers")}
              text={data.antalPassagerare + " " + t("global.pc", "pcs")}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

export const Service = ({ data }) => {
  return (
    <Grid container>
      <Grid item xs={12} direction="column" display="flex" gap={2}>
        <Grid container direction="row" display="flex" rowSpacing={2} columnSpacing={4} flexW>
          <Grid item>
            <TextCell2
              label={t("vehicleregistry.inspection_group", "Inspection group")}
              text={data.besiktningsgrupp}
            />
          </Grid>
          <Grid item>
            <TextCell2
              label={t("global.inspection_last", "Inspection last")}
              text={
                data.besiktningsdatumtom && (
                  <Typography variant="bodyMedium" fontWeight={600}>
                    {moment.utc(data.besiktningsdatumtom).local().format("YYYY-MM-DD")}
                  </Typography>
                )
              }
            />
          </Grid>

          <Grid item>
            <TextCell2
              label={t("vehicleregistry.previous_inspection", "Previous inspection")}
              text={
                data.foregaendeBesiktningsdatum &&
                moment.utc(data.foregaendeBesiktningsdatum).isAfter(moment.utc("0001-01-01")) && (
                  <Typography variant="bodyMedium" fontWeight={600}>
                    {moment.utc(data.foregaendeBesiktningsdatum).local().format("YYYY-MM-DD")}
                  </Typography>
                )
              }
            />
          </Grid>
          <Grid item>
            <TextCell2
              label={t(
                "vehicleregistry.previous_inspection_program_code",
                "Previous inspection program code"
              )}
              text={data.foregBesiktningsprogramkod}
            />
          </Grid>

          <Grid item>
            <TextCell2
              label={t("vehicleregistry.inspection_station", "Inspection station")}
              text={data.besiktningsstation}
            />
          </Grid>
          <Grid item>
            <TextCell2
              label={t(
                "vehicleregistry.meter_reading_previous_inspection",
                "Meter reading previous inspection"
              )}
              text={data.foregaendeKorstracka}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default TechnicalData
